import React, { useEffect } from 'react';
import { FiAlertTriangle, FiX } from 'react-icons/fi';
import { useTheme } from '../../context/ThemeContext';

const ConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  warningText, 
  confirmText = "Deletar", 
  cancelText = "Cancelar",
  isLoading = false
}) => {
  const { isDarkMode } = useTheme();

  // Bloquear scroll do body quando o modal estiver aberto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay com Blur */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={!isLoading ? onClose : undefined}
      ></div>

      {/* Modal Container */}
      <div 
        className={`
          relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden
          transform transition-all animate-in fade-in zoom-in duration-200
          ${isDarkMode ? 'bg-gray-900 border border-gray-800' : 'bg-white'}
        `}
      >
        {/* Modal Header */}
        <div className={`flex items-start justify-between p-5 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <FiAlertTriangle className="w-5 h-5 text-red-600 dark:text-red-500" />
            </div>
            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {title}
            </h3>
          </div>
          <button 
            onClick={onClose}
            disabled={isLoading}
            className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-gray-400 hover:bg-gray-800 hover:text-white' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'} disabled:opacity-50`}
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5">
          <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
            {message}
          </p>
          
          {warningText && (
            <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-orange-900/20 border-orange-800/50' : 'bg-orange-50 border-orange-100'}`}>
              <div className="flex gap-2.5">
                <FiAlertTriangle className={`w-5 h-5 flex-shrink-0 ${isDarkMode ? 'text-orange-500' : 'text-orange-600'}`} />
                <p className={`text-sm font-medium ${isDarkMode ? 'text-orange-300' : 'text-orange-800'}`}>
                  {warningText}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className={`px-5 py-4 flex flex-col-reverse sm:flex-row justify-end gap-3 ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
          <button
            onClick={onClose}
            disabled={isLoading}
            className={`
              w-full sm:w-auto px-5 py-2.5 rounded-xl font-medium text-sm transition-colors
              ${isDarkMode 
                ? 'bg-transparent text-gray-300 hover:bg-gray-800 border border-gray-700' 
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
              }
              focus:outline-none focus:ring-2 focus:ring-gray-200 disabled:opacity-50
            `}
          >
            {cancelText}
          </button>
          
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`
              relative w-full sm:w-auto px-5 py-2.5 rounded-xl font-medium text-sm text-white transition-all
              bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600
              focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2
              shadow-md shadow-red-500/20 disabled:opacity-70 flex items-center justify-center
            `}
          >
            {isLoading ? (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : null}
            {isLoading ? 'Excluindo...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
